import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { t } from '../i18n';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5003';

interface ImageSelectionPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess?: () => void;
  listingId: string;
  listingTitle: string;
  xmlLink: string;
  username: string;
}

interface ListingImage {
  id: string;
  url: string;
  alt?: string;
  loaded?: boolean;
  retouchedUrl?: string; // For starcars user - retouched image URL from API
}

const PopupOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 16px;
  
  @media (max-width: 600px) {
    padding: 8px;
    align-items: flex-start;
  }
`;

const PopupContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 800px;
  max-height: 80vh;
  width: 90%;
  overflow-y: auto;
  position: relative;
  
  @media (max-width: 600px) {
    padding: 16px;
    width: 100%;
    max-height: 90vh;
    border-radius: 8px;
    margin-top: 20px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    margin-top: 10px;
  }
`;

const PopupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 1px solid #eee;
  padding-bottom: 16px;
  
  @media (max-width: 600px) {
    margin-bottom: 16px;
    padding-bottom: 12px;
  }
`;

const PopupTitle = styled.h2`
  margin: 0;
  color: #333;
  font-size: 20px;
  word-break: break-word;
  
  @media (max-width: 600px) {
    font-size: 18px;
    line-height: 1.3;
  }
  
  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  flex-shrink: 0;
  
  &:hover {
    background: #f0f0f0;
    color: #333;
  }
  
  @media (max-width: 600px) {
    width: 36px;
    height: 36px;
    font-size: 20px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #666;
  flex-direction: column;
  gap: 10px;
  
  @media (max-width: 600px) {
    height: 150px;
    gap: 8px;
  }
`;

const ErrorContainer = styled.div`
  color: #e74c3c;
  text-align: center;
  padding: 20px;
  
  @media (max-width: 600px) {
    padding: 16px;
    font-size: 14px;
  }
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
  
  @media (max-width: 600px) {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 12px;
    margin-bottom: 16px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 10px;
  }
`;

const ImageContainer = styled.div<{ isSelected: boolean }>`
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s ease;
  border: 2px solid ${props => props.isSelected ? '#007bff' : '#ddd'};
  background: white;
  box-shadow: ${props => props.isSelected 
    ? '0 4px 12px rgba(0, 123, 255, 0.2)' 
    : '0 2px 6px rgba(0, 0, 0, 0.1)'
  };
  cursor: pointer;
  
  &:hover {
    border-color: ${props => props.isSelected ? '#0056b3' : '#999'};
    box-shadow: ${props => props.isSelected 
      ? '0 4px 12px rgba(0, 123, 255, 0.3)' 
      : '0 2px 8px rgba(0, 0, 0, 0.15)'
    };
  }
  
  @media (max-width: 600px) {
    border-radius: 6px;
  }
`;

const LazyImage = styled.img<{ loaded: boolean }>`
  width: 100%;
  height: 120px;
  object-fit: cover;
  display: block;
  opacity: ${props => props.loaded ? 1 : 0};
  transition: opacity 0.3s ease;
  user-select: none;
  pointer-events: none;
  
  @media (max-width: 600px) {
    height: 100px;
  }
  
  @media (max-width: 480px) {
    height: 80px;
  }
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 120px;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 14px;
  user-select: none;
  pointer-events: none;
  
  @media (max-width: 600px) {
    height: 100px;
    font-size: 12px;
  }
  
  @media (max-width: 480px) {
    height: 80px;
    font-size: 11px;
  }
`;

const SelectionCheckbox = styled.input`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  cursor: pointer;
  z-index: 1000;
  accent-color: #007bff;
  pointer-events: auto;
  background: white;
  border: 2px solid #ccc;
  margin: 0;
  
  &:checked {
    background: #007bff;
    border-color: #007bff;
  }
  
  @media (max-width: 600px) {
    top: 6px;
    right: 6px;
    width: 18px;
    height: 18px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  border-top: 1px solid #eee;
  padding-top: 16px;
  
  @media (max-width: 600px) {
    flex-direction: column;
    gap: 8px;
    padding-top: 12px;
    
    & > * {
      width: 100%;
    }
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  
  ${props => props.variant === 'primary' ? `
    background: #007bff;
    color: white;
    
    &:hover {
      background: #0056b3;
    }
  ` : `
    background: #f8f9fa;
    color: #333;
    border: 1px solid #ddd;
    
    &:hover {
      background: #e9ecef;
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  @media (max-width: 600px) {
    padding: 12px 16px;
    font-size: 16px;
  }
`;

const ProgressInfo = styled.div`
  color: #666;
  font-size: 14px;
`;

const DescriptionSection = styled.div`
  margin-bottom: 20px;
  border-top: 1px solid #eee;
  padding-top: 20px;
`;

const DescriptionTitle = styled.h3`
  margin: 0 0 10px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
`;

const DescriptionTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.2);
  }
  
  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }
`;

const DescriptionActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
`;

const DescriptionSaveButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #218838;
  }
  
  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
  }
`;

const DescriptionStatus = styled.span<{ saved?: boolean }>`
  font-size: 12px;
  color: ${props => props.saved ? '#28a745' : '#666'};
  font-style: italic;
`;

const ImageSelectionPopup: React.FC<ImageSelectionPopupProps> = ({
  isOpen,
  onClose,
  onSaveSuccess,
  listingId,
  listingTitle,
  xmlLink,
  username
}) => {
  const [images, setImages] = useState<ListingImage[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loadedCount, setLoadedCount] = useState(0);
  const observerRef = useRef<IntersectionObserver | null>(null);
  
  // Description state for autocentrumbb user
  const [description, setDescription] = useState<string>('');
  const [descriptionLoading, setDescriptionLoading] = useState(false);
  const [descriptionSaving, setDescriptionSaving] = useState(false);
  const [hasDescriptionChanged, setHasDescriptionChanged] = useState(false);

  // State for starcars user - retouched images functionality
  const [originalImages, setOriginalImages] = useState<ListingImage[]>([]);
  const [isRetouchedView, setIsRetouchedView] = useState(false);
  const [removingLogos, setRemovingLogos] = useState(false);
  const [retouchedImages, setRetouchedImages] = useState<ListingImage[]>([]);

  useEffect(() => {
    if (isOpen) {
      // Clear previous listing data immediately when opening for a new listing
      setImages([]);
      setSelectedIds([]);
      setError(null);
      setLoadedCount(0);
      
      // Clear description state
      setDescription('');
      setHasDescriptionChanged(false);
      
      // Clear starcars state
      setOriginalImages([]);
      setIsRetouchedView(false);
      setRetouchedImages([]);
      
      // Check if this is starcars user and if logo removal has been processed
      if (username === 'starcars') {
        checkLogoRemovalStatus();
      } else {
        // For other users, fetch images normally
        fetchListingImages();
        loadSavedImageSelection();
      }
      
      // Load description for autocentrumbb user
      if (username === 'autocentrumbb') {
        loadListingDescription();
      }
    } else {
      // Clear data when popup is closed to prevent memory leaks
      setImages([]);
      setError(null);
      setLoadedCount(0);
      setDescription('');
      setHasDescriptionChanged(false);
      setOriginalImages([]);
      setIsRetouchedView(false);
      setRetouchedImages([]);
    }
  }, [isOpen, listingId, xmlLink, username]);

  const fetchListingImages = async () => {
    setLoading(true);
    setError('');
    setImages([]);
    setLoadedCount(0);
    
    try {
      console.log(`Fetching images for listing ${listingId} from ${xmlLink}`);
      
      const response = await axios.get(`${API_URL}/api/listing-images`, {
        params: {
          xmlLink,
          listingId
        }
      });

      if (response.data && response.data.success) {
        const imagesData = response.data.images.map((img: any) => ({
          ...img,
          loaded: false
        }));
        
        console.log(`Fetched ${imagesData.length} images for listing ${listingId}`);
        setImages(imagesData);
      }
    } catch (err) {
      console.error('Error fetching images:', err);
      setError('Failed to load images. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadSavedImageSelection = async () => {
    try {
      console.log(`🔍 LOADING - Starting load process for user: ${username}, listing: ${listingId}`);
      
      const response = await axios.get(`${API_URL}/api/listing-images/${username}/${listingId}`);
      
      console.log(`🔍 LOADING - Server response:`, response.data);
      console.log(`🔍 LOADING - Response status:`, response.status);
      
      if (response.data && response.data.success) {
        const savedImageIds = response.data.savedImageIds || [];
        console.log(`🔍 LOADING - Found ${savedImageIds.length} saved image selections:`, savedImageIds);
        console.log(`🔍 LOADING - Last updated:`, response.data.imageSelectionUpdated);
        
        setSelectedIds(savedImageIds);
        console.log(`✅ LOADING - Successfully loaded saved selections into state`);
      } else {
        console.log(`⚠️ LOADING - No saved selections found or server returned success=false`);
      }
    } catch (err) {
      console.error(`❌ LOADING ERROR - Failed to load saved image selection:`, err);
      if (axios.isAxiosError(err)) {
        console.error(`❌ LOADING ERROR - Axios error details:`, {
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data
        });
      }
      // Don't show error to user for this - just continue with empty selection
    }
  };

  const handleImageLoad = useCallback((imageId: string) => {
    setImages(prev => prev.map(img => 
      img.id === imageId ? { ...img, loaded: true } : img
    ));
    setLoadedCount(prev => prev + 1);
  }, []);

  const handleImageError = useCallback((imageId: string) => {
    console.warn(`Failed to load image: ${imageId}`);
    setImages(prev => prev.filter(img => img.id !== imageId));
  }, []);

  const toggleImageSelection = useCallback((imageId: string) => {
    console.log(`Clicking on image: ${imageId}`);
    
    setSelectedIds(prev => {
      console.log(`Current selectedIds before toggle:`, prev);
      const isCurrentlySelected = prev.includes(imageId);
      const newSelection = isCurrentlySelected 
        ? prev.filter(id => id !== imageId)
        : [...prev, imageId];
      
      console.log(`Image ${imageId} ${!isCurrentlySelected ? 'SELECTED' : 'DESELECTED'}. Total selected: ${newSelection.length}`);
      console.log(`New selection array:`, newSelection);
      return newSelection;
    });
  }, []);

  // A saved selection holds positional photo ids ({listingId}_photo_{n}). If the
  // listing's photo list has since changed, some of them may point at photos
  // that no longer exist - count and save only the ones that still do, and say
  // how many were dropped rather than silently showing a number nothing matches.
  const currentImageIds = new Set(images.map(image => image.id));
  const validSelectedIds = selectedIds.filter(id => currentImageIds.has(id));
  const orphanedSelectionCount = images.length > 0 ? selectedIds.length - validSelectedIds.length : 0;

  const saveImageSelection = async () => {
    setSaving(true);
    
    try {
      console.log(`🔄 SAVING - Starting save process for listing ${listingId}`);
      console.log(`🔄 SAVING - Username: ${username}`);
      console.log(`🔄 SAVING - Selected IDs count: ${selectedIds.length}`);
      console.log(`🔄 SAVING - Selected IDs array:`, selectedIds);
      
      // Special handling for starcars user when using retouched images
      if (username === 'starcars' && isRetouchedView) {
        console.log(`🔄 SAVING - STARCARS - Saving retouched images to Google Cloud links`);
        
        // Get Google Cloud links for selected retouched images
        const selectedRetouchedImages = retouchedImages.filter(img => selectedIds.includes(img.id));
        const googleCloudLinks = selectedRetouchedImages.map(img => img.retouchedUrl).filter(Boolean);
        
        const requestData = {
          username,
          listingId,
          googleCloudLinks,
          isRetouchedImages: true
        };
        
        console.log(`🔄 SAVING - STARCARS - Request payload:`, requestData);
        
        // Use special endpoint for starcars retouched images
        const response = await axios.post(`${API_URL}/api/update-listing-retouched-images`, requestData);
        
        if (response.data.success) {
          console.log(`✅ SUCCESS - STARCARS - Saved ${googleCloudLinks.length} retouched image links for listing ${listingId}`);
          setTimeout(() => {
            console.log(`🚪 CLOSING - Popup closing after successful starcars save`);
            onClose();
          }, 500);
        } else {
          console.error(`❌ SAVE FAILED - STARCARS - Server returned success=false:`, response.data.message);
          setError(response.data.message || 'Failed to save retouched image selection');
        }
      } else {
        // Normal saving process for all other users or starcars with original images
        const requestData = {
          username,
          listingId,
          imageIds: validSelectedIds
        };
        console.log(`🔄 SAVING - Request payload:`, requestData);
        
        const response = await axios.post(`${API_URL}/api/update-listing-images`, requestData);
        
        console.log(`✅ SAVING - Server response:`, response.data);
        console.log(`✅ SAVING - Response status:`, response.status);
        
        if (response.data.success) {
          console.log(`✅ SUCCESS - Saved ${selectedIds.length} image selections for listing ${listingId}`);
          console.log(`✅ SUCCESS - Saved image IDs:`, response.data.selectedImages);
          
          // Add a small delay to ensure the data is written to database
          setTimeout(() => {
            console.log(`🚪 CLOSING - Popup closing after successful save`);
            onClose();
          }, 500);
        } else {
          console.error(`❌ SAVE FAILED - Server returned success=false:`, response.data.message);
          setError(response.data.message || 'Failed to save image selection');
        }
      }
    } catch (err) {
      console.error(`❌ SAVE ERROR - Network or server error:`, err);
      if (axios.isAxiosError(err)) {
        console.error(`❌ SAVE ERROR - Axios error details:`, {
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data
        });
        setError(`Failed to save selection: ${err.response?.data?.message || err.message}`);
      } else {
        setError('Failed to save selection. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const loadListingDescription = async () => {
    try {
      setDescriptionLoading(true);
      console.log(`🔍 DESCRIPTION - Loading description for listing ${listingId}`);
      
      // First try to load saved custom description
      const savedResponse = await axios.get(`${API_URL}/api/listing-description/${username}/${listingId}`);
      if (savedResponse.data && savedResponse.data.success && savedResponse.data.customDescription) {
        console.log(`✅ DESCRIPTION - Found saved custom description`);
        setDescription(savedResponse.data.customDescription);
        setDescriptionLoading(false);
        return;
      }
      
      // If no saved description, load from XML
      console.log(`🔍 DESCRIPTION - Loading original description from XML`);
      const xmlResponse = await axios.get(`${API_URL}/api/listing-description-xml`, {
        params: {
          xmlLink,
          listingId
        }
      });
      
      if (xmlResponse.data && xmlResponse.data.success) {
        console.log(`✅ DESCRIPTION - Loaded description from XML`);
        setDescription(xmlResponse.data.description || '');
      } else {
        console.log(`⚠️ DESCRIPTION - No description found in XML`);
        setDescription('');
      }
    } catch (err) {
      console.error(`❌ DESCRIPTION ERROR - Failed to load description:`, err);
      setDescription('');
    } finally {
      setDescriptionLoading(false);
    }
  };

  const saveDescription = async () => {
    if (username !== 'autocentrumbb' || !hasDescriptionChanged) return;
    
    setDescriptionSaving(true);
    
    try {
      console.log(`🔄 DESCRIPTION SAVE - Saving custom description for listing ${listingId}`);
      
      const response = await axios.post(`${API_URL}/api/save-listing-description`, {
        username,
        listingId,
        customDescription: description
      });
      
      if (response.data.success) {
        console.log(`✅ DESCRIPTION SAVE - Successfully saved custom description`);
        setHasDescriptionChanged(false);
      } else {
        console.error(`❌ DESCRIPTION SAVE FAILED:`, response.data.message);
        setError(response.data.message || 'Failed to save description');
      }
    } catch (err) {
      console.error(`❌ DESCRIPTION SAVE ERROR:`, err);
      setError('Failed to save description. Please try again.');
    } finally {
      setDescriptionSaving(false);
    }
  };

  const handleDescriptionChange = (newDescription: string) => {
    setDescription(newDescription);
    setHasDescriptionChanged(true);
  };

  // Function to remove logos - for starcars user only
  // Updated: Fixed CORS issue by using backend proxy
  // DEPLOYMENT CHECK: If you see this comment, the new code is deployed!
  const removeLogos = async () => {
    if (username !== 'starcars') return;
    
    console.log('🚀 NEW CODE DEPLOYED - Using backend proxy for image download');
    setRemovingLogos(true);
    setError(null);
    
    try {
      console.log(`🔄 STARCARS - Starting logo removal process for listing ${listingId}`);
      
      // Store original images if not already stored
      if (originalImages.length === 0) {
        setOriginalImages([...images]);
      }
      
      // Get image URLs to send to backend
      const imageUrls = images.map(img => img.url);
      console.log(`🔄 STARCARS - Sending ${imageUrls.length} image URLs to backend for processing`);
      
      try {
        console.log(`🔄 STARCARS - Calling backend to handle image download and logo removal`);
        
        // Call our backend to handle the logo removal process
        // Backend will download images and forward to AI service
        const response = await axios.post(`${API_URL}/api/process-logo-removal`, {
          username,
          listingId,
          imageUrls: imageUrls
        });
        
        console.log(`🔄 STARCARS - Backend response:`, response.data);
        
        if (response.data.success) {
          console.log(`✅ STARCARS - Logo removal completed successfully`);
          console.log(`✅ STARCARS - Processed ${response.data.processedCount}/${response.data.totalImages} images`);
          
          // Update the listing state to indicate logo removal is complete
          setIsRetouchedView(true);
          
          // The images will now be loaded from Google Cloud Storage
          // Trigger a reload of images from GCS
          await loadImagesFromGCS();
          
          console.log(`✅ STARCARS - Successfully switched to retouched view`);
        } else {
          console.error(`❌ STARCARS - Logo removal failed:`, response.data.message);
          setError(response.data.message || 'Failed to remove logos');
        }
        
      } catch (processingError) {
        console.error(`❌ STARCARS - Error during image processing:`, processingError);
        if (axios.isAxiosError(processingError)) {
          const errorMsg = processingError.response?.data?.message || processingError.message;
          setError(`Failed to process images: ${errorMsg}`);
        } else {
          setError('Failed to process images for logo removal. Please try again.');
        }
      }
      
    } catch (err) {
      console.error(`❌ STARCARS - Logo removal error:`, err);
      setError('Failed to process logo removal. Please try again.');
    } finally {
      setRemovingLogos(false);
    }
  };

  // Function to load images from Google Cloud Storage - for starcars user only
  const loadImagesFromGCS = async () => {
    if (username !== 'starcars') return;
    
    try {
      console.log(`🔄 STARCARS - Loading processed images from Google Cloud Storage for listing ${listingId}`);
      
      const response = await axios.get(`${API_URL}/api/get-gcs-images/${listingId}`, {
        params: { username }
      });
      
      if (response.data.success && response.data.images) {
        console.log(`✅ STARCARS - Loaded ${response.data.images.length} processed images from GCS`);
        
        // Convert GCS images to our image format
        const gcsImages = response.data.images.map((gcsImg: any, index: number) => ({
          id: `${listingId}_gcs_${index}`,
          url: gcsImg.public_url,
          alt: gcsImg.filename,
          loaded: false,
          retouchedUrl: gcsImg.public_url
        }));
        
        setRetouchedImages(gcsImages);
        setImages(gcsImages);
        setIsRetouchedView(true);
        setLoadedCount(0); // Reset loaded count for new images
        
        console.log(`✅ STARCARS - Successfully loaded retouched images from GCS`);
      } else {
        console.error(`❌ STARCARS - Failed to load GCS images:`, response.data.message);
        setError('Failed to load processed images. Please try again.');
      }
    } catch (error) {
      console.error(`❌ STARCARS - Error loading GCS images:`, error);
      setError('Failed to load processed images from Google Cloud Storage.');
    }
  };

  // Function to revert to original images - for starcars user only
  const revertToOriginal = () => {
    if (username !== 'starcars' || originalImages.length === 0) return;
    
    console.log(`🔄 STARCARS - Reverting to original images`);
    setImages([...originalImages]);
    setIsRetouchedView(false);
    setRetouchedImages([]);
    setError(null);
    console.log(`✅ STARCARS - Successfully reverted to original images`);
  };

  // Function to check logo removal status for starcars user
  const checkLogoRemovalStatus = async () => {
    if (username !== 'starcars') return;
    
    try {
      console.log(`🔍 STARCARS - Checking logo removal status for listing ${listingId}`);
      
      const response = await axios.get(`${API_URL}/api/check-logo-removal-status/${listingId}`, {
        params: { username }
      });
      
      if (response.data.success) {
        const hasLogoRemoval = response.data.logo_remove === true;
        console.log(`🔍 STARCARS - Logo removal status: ${hasLogoRemoval ? 'Processed' : 'Not processed'}`);
        
        if (hasLogoRemoval) {
          // Logo removal has been processed, load images from GCS
          await loadImagesFromGCS();
        } else {
          // Logo removal not processed, load original images from XML
          await fetchListingImages();
          await loadSavedImageSelection();
        }
      } else {
        console.log(`🔍 STARCARS - No logo removal status found, loading original images`);
        await fetchListingImages();
        await loadSavedImageSelection();
      }
    } catch (error) {
      console.error(`❌ STARCARS - Error checking logo removal status:`, error);
      // Fallback to loading original images
      await fetchListingImages();
      await loadSavedImageSelection();
    }
  };

  if (!isOpen) return null;

  return (
    <PopupOverlay isOpen={isOpen} onClick={onClose}>
      <PopupContainer onClick={e => e.stopPropagation()}>
        <PopupHeader>
          <PopupTitle>{t('Select Images')} - {listingTitle}</PopupTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </PopupHeader>

        {loading && (
          <LoadingContainer>
            <div>{t('Loading images...')}</div>
            <div>{t('This may take a moment while we process the XML feed')}</div>
          </LoadingContainer>
        )}

        {error && (
          <ErrorContainer>
            {t(error)}
          </ErrorContainer>
        )}

        {!loading && !error && images.length === 0 && (
          <ErrorContainer>
            {t('No images found for this listing.')}
          </ErrorContainer>
        )}

        {!loading && !error && images.length > 0 && (
          <>
            <ImageGrid>
              {images.map((image) => {
                const isSelected = selectedIds.includes(image.id);
                
                return (
                  <ImageContainer
                    key={image.id}
                    isSelected={isSelected}
                    onClick={(e) => {
                      console.log(`IMAGE CONTAINER CLICKED for: ${image.id}`);
                      // Only toggle if the click wasn't on the checkbox
                      if (e.target !== e.currentTarget.querySelector('input[type="checkbox"]')) {
                        toggleImageSelection(image.id);
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {!image.loaded && (
                      <ImagePlaceholder>
                        {t('Loading...')}
                      </ImagePlaceholder>
                    )}
                    <LazyImage
                      src={image.url}
                      alt={image.alt || 'Listing image'}
                      loaded={image.loaded || false}
                      onLoad={() => handleImageLoad(image.id)}
                      onError={() => handleImageError(image.id)}
                      loading="lazy"
                      draggable={false}
                    />
                    <SelectionCheckbox
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        console.log(`CHECKBOX CLICKED for image: ${image.id}`);
                        console.log(`Checkbox checked state: ${e.target.checked}`);
                        console.log(`Current isSelected: ${isSelected}`);
                        e.stopPropagation();
                        toggleImageSelection(image.id);
                      }}
                      onClick={(e) => {
                        console.log(`CHECKBOX CLICKED (onClick) for image: ${image.id}`);
                        e.stopPropagation();
                      }}
                    />
                  </ImageContainer>
                );
              })}
            </ImageGrid>

            {/* Description editor for autocentrumbb user only */}
            {false && username === 'autocentrumbb' && (
              <DescriptionSection>
                <DescriptionTitle>Edit Description</DescriptionTitle>
                {descriptionLoading ? (
                  <div>Loading description...</div>
                ) : (
                  <>
                    <DescriptionTextarea
                      value={description}
                      onChange={(e) => handleDescriptionChange(e.target.value)}
                      placeholder="Enter car description..."
                      disabled={descriptionSaving}
                    />
                    <DescriptionActions>
                      <DescriptionStatus saved={!hasDescriptionChanged}>
                        {hasDescriptionChanged ? 'Unsaved changes' : 'Saved'}
                      </DescriptionStatus>
                      <DescriptionSaveButton
                        onClick={saveDescription}
                        disabled={!hasDescriptionChanged || descriptionSaving}
                      >
                        {descriptionSaving ? 'Saving...' : 'Save Description'}
                      </DescriptionSaveButton>
                    </DescriptionActions>
                  </>
                )}
              </DescriptionSection>
            )}

            <ActionButtons>
              <ProgressInfo>
                {t('{loaded} of {total} images loaded', { loaded: loadedCount, total: images.length })}
                {orphanedSelectionCount > 0 && (
                  <span style={{ color: '#ef6c00', marginLeft: '8px' }}>
                    · {t('{count} previously selected photos no longer exist', { count: orphanedSelectionCount })}
                  </span>
                )}
              </ProgressInfo>
              
              {/* Special button for starcars user */}
              {username === 'starcars' && (
                <Button 
                  variant="secondary" 
                  onClick={isRetouchedView ? revertToOriginal : removeLogos}
                  disabled={removingLogos}
                  style={{ 
                    background: isRetouchedView ? '#ffc107' : '#28a745',
                    color: 'white',
                    border: 'none'
                  }}
                >
                  {removingLogos 
                    ? 'Processing...' 
                    : isRetouchedView 
                      ? 'Revert to Original' 
                      : 'Remove Logo v2.0'
                  }
                </Button>
              )}
              
              <div style={{ display: 'flex', gap: '12px' }}>
                <Button variant="secondary" onClick={onClose}>
                  {t('Cancel')}
                </Button>
                <Button 
                  variant="primary" 
                  onClick={saveImageSelection}
                  disabled={saving}
                >
                  {saving ? t('Saving...') : `${t('Save Selection')} (${validSelectedIds.length})`}
                </Button>
              </div>
            </ActionButtons>
          </>
        )}
      </PopupContainer>
    </PopupOverlay>
  );
};

export default ImageSelectionPopup; 